import { CheckIcon } from '@diligentcorp/atlas-react-icons';
import {
  CreateIssueSubtypeRequestDto,
  UpdateIssueSubtypeRequestDto,
  UpdateIssueTypeRequestDto,
} from '@issuemanager/api-client';
import {
  ConfirmationModal,
  ControlledTextField,
  LoadingProgress,
} from '@issuemanager/apps/client/components';
import {
  APP_ERROR_TYPE,
  AppHeaderPrimaryButtonProps,
  ERROR_USER_INTERFACE,
  useAppErrorHandler,
  useHeaderStore,
} from '@issuemanager/apps/client/contexts';
import { paths } from '@issuemanager/apps/client/paths';
import { useLocalizedIssueTypes } from '@issuemanager/client/features/issues';
import { IssueType, SUBTYPE_NAME_LENGTH_MAX } from '@issuemanager/constants';
import { FormLabel, Stack } from '@mui/material';
import { useEffect, useMemo, useState, type JSX } from 'react';
import {
  Controller,
  FieldValues,
  RegisterOptions,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useCreateIssueSubtype,
  useDeleteIssueSubtype,
  useIssueSubtype,
  useUpdateIssueSubtype,
} from '../../../features/issue-subtypes';
import {
  useInfiniteIssueTypes,
  useIssueType,
  useUpdateIssueType,
} from '../../../features/issue-types';
import { TypeManagersSelector } from './components/user-selector';

const ISSUE_TYPE_PARENT_NONE_ID = 'none';

type IssueTypeFormDto = UpdateIssueTypeRequestDto & {
  name?: string;
  parent: string;
};

const emptyDefaultValues = {
  name: '',
  parent: '',
  issueTypeManagers: [],
};

function IssueTypeForm(): JSX.Element {
  const { t } = useTranslation(['common', 'settings']);
  const navigate = useNavigate();
  const { setHeaderProps } = useHeaderStore();

  const [searchParams] = useSearchParams();
  const localizedIssueTypes = useLocalizedIssueTypes();
  const appErrorHandler = useAppErrorHandler();
  const issueTypeId = searchParams.get('issueTypeId') || undefined;
  const subtypeId = searchParams.get('issueSubtypeId') || undefined;
  const hasId = Boolean(issueTypeId || subtypeId);

  const [issueTypeDefaultValues, setIssueTypeDefaultValues] =
    useState<IssueTypeFormDto>();
  const [subtypeDefaultValues, setSubtypeDefaultValues] =
    useState<IssueTypeFormDto>();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const onSubmit = async (data: FieldValues) => {
    const subAction = hasId ? editSubtype : addSubtype;

    return data.parent === ISSUE_TYPE_PARENT_NONE_ID
      ? editIssueType(data)
      : subAction(data);
  };

  const onDelete = () => {
    navigate(`/${paths.settings.index}/${paths.issueTypes.index}`);
  };

  const buildIssueTypeBase = (
    data: FieldValues,
  ): UpdateIssueTypeRequestDto => ({
    issueTypeManagers: data.issueTypeManagers,
  });

  const editIssueType = async (data: FieldValues) => {
    const issueType: UpdateIssueTypeRequestDto & { id: string } = {
      ...buildIssueTypeBase(data),
      id: issueTypeId!,
    };
    await updateIssueType.mutateAsync(issueType);
    navigate(`/${paths.settings.index}/${paths.issueTypes.index}`);
  };

  const buildSubtypeBase = (data: FieldValues) => ({
    name: data.name,
    issueTypeId: data.parent,
    issueTypeManagers: data.issueTypeManagers,
  });

  const addSubtype = async (data: FieldValues) => {
    const issueSubtype: CreateIssueSubtypeRequestDto = buildSubtypeBase(
      data,
    ) as CreateIssueSubtypeRequestDto;
    await createSubtype.mutateAsync(issueSubtype);
    navigate(`/${paths.settings.index}/${paths.issueTypes.index}`);
  };

  const editSubtype = async (data: FieldValues) => {
    if (subtypeId === undefined) return;

    const issueSubtype: UpdateIssueSubtypeRequestDto & { id: string } = {
      id: subtypeId,
      ...buildSubtypeBase(data),
    };

    await updateSubtype.mutateAsync(issueSubtype);

    navigate(`/${paths.settings.index}/${paths.issueTypes.index}`);
  };

  const handleSubmit = () => {
    form.handleSubmit(onSubmit)();
  };

  const handleDeleteIssueType = async () => {
    if (subtypeId) {
      await deleteSubtype.mutateAsync(subtypeId);
    }
    onDelete();
    handleCloseConfirmationModal();
  };

  const handleOpenConfirmationModal = () => {
    setShowConfirmationModal(true);
  };

  const handleCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  const {
    error: issueTypeError,
    data: issueTypeResponse,
    isLoading: isIssueTypeLoading,
    dataUpdatedAt: issueTypeDataUpdatedAt,
    errorUpdatedAt: issueTypeErrorUpdatedAt,
  } = useIssueType({ id: issueTypeId ?? '' });

  const {
    error: subtypeError,
    data: subtypeResponse,
    isLoading: isSubtypeLoading,
    dataUpdatedAt: subtypeDataUpdatedAt,
    errorUpdatedAt: subtypeErrorUpdatedAt,
  } = useIssueSubtype({ id: subtypeId ?? '' });

  const createSubtype = useCreateIssueSubtype();

  const updateIssueType = useUpdateIssueType();

  const updateSubtype = useUpdateIssueSubtype();

  const issueTypesQuery = useInfiniteIssueTypes({
    params: {
      filterByEnabled: true,
      sortByName: 'asc',
    },
    enabled: !issueTypeId,
  });

  const isCreateOrUpdateLoading =
    createSubtype.isPending ||
    updateIssueType.isPending ||
    updateSubtype.isPending;

  const isLoading = isIssueTypeLoading || isSubtypeLoading;

  const addEditButtonText = useMemo(() => {
    if (hasId) {
      return isCreateOrUpdateLoading
        ? t('settings:issueTypes.form.issueType.actions.editing')
        : t('settings:issueTypes.form.issueType.actions.edit');
    }

    return isCreateOrUpdateLoading
      ? t('settings:issueTypes.form.issueType.actions.adding')
      : t('settings:issueTypes.form.issueType.actions.add');
  }, [hasId, isCreateOrUpdateLoading]);

  const addEditButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      variant: 'contained',
      startIcon: <CheckIcon />,
      onClick: handleSubmit,
      loading: isCreateOrUpdateLoading,
      children: addEditButtonText,
      'data-testid': 'add-category-button',
    }),
    [isCreateOrUpdateLoading, addEditButtonText],
  );

  const deleteButtonProps: AppHeaderPrimaryButtonProps = useMemo(
    () => ({
      variant: 'outlined',
      onClick: handleOpenConfirmationModal,
      children: t('settings:issueTypes.form.issueType.actions.delete'),
      'data-testid': 'delete-category-button',
      disabled: isCreateOrUpdateLoading,
    }),
    [isCreateOrUpdateLoading],
  );

  const pageTitle = useMemo(() => {
    if (!hasId) {
      return 'add';
    }

    if (subtypeId) {
      return 'editSubtype';
    }

    return 'editType';
  }, [hasId, subtypeId]);

  const canDelete = useMemo(() => {
    if (hasId && subtypeId) return true;
    return false;
  }, [hasId, subtypeId]);

  useEffect(() => {
    setHeaderProps({
      viewTitle: {
        breadcrumbSections: [
          {
            url: '/',
            title: t('common:breadcrumbs.issueManager'),
          },
          {
            url: `/${paths.settings.index}/${paths.issueTypes.index}`,
            title: t('settings:routeName'),
          },
        ],
        title: t(`settings:issueTypes.form.issueType.title.${pageTitle}`),
      },
      buttons: [...(canDelete ? [deleteButtonProps] : []), addEditButtonProps],
    });
  }, [addEditButtonProps, deleteButtonProps, hasId, pageTitle]);

  const deleteSubtype = useDeleteIssueSubtype({});

  const isDeleteLoading = deleteSubtype.isPending;

  useEffect(() => {
    if (issueTypeError?.response?.status === 404) {
      appErrorHandler.setError({
        type: APP_ERROR_TYPE.NOT_FOUND,
        userInterface: ERROR_USER_INTERFACE.PAGE,
      });
    } else if (issueTypeResponse) {
      setIssueTypeDefaultValues({
        name:
          localizedIssueTypes[issueTypeResponse.name as IssueType] ??
          issueTypeResponse.name,
        parent: ISSUE_TYPE_PARENT_NONE_ID,
        issueTypeManagers: issueTypeResponse.issueTypeManagers,
      });
    }
  }, [issueTypeDataUpdatedAt, issueTypeErrorUpdatedAt]);

  useEffect(() => {
    if (subtypeError?.response?.status === 404) {
      appErrorHandler.setError({
        type: APP_ERROR_TYPE.NOT_FOUND,
        userInterface: ERROR_USER_INTERFACE.PAGE,
      });
    } else if (subtypeResponse) {
      setSubtypeDefaultValues({
        name: subtypeResponse.name,
        parent: subtypeResponse.issueType?.id ?? '',
        issueTypeManagers: subtypeResponse.issueTypeManagers ?? [],
      });
    }
  }, [subtypeDataUpdatedAt, subtypeErrorUpdatedAt]);

  const defaultValues =
    issueTypeDefaultValues ?? subtypeDefaultValues ?? emptyDefaultValues;

  const form = useForm<IssueTypeFormDto>({
    mode: 'onSubmit',
    defaultValues,
  });

  useEffect(() => {
    // Update form default values when formData changes
    form.reset(defaultValues);
  }, [defaultValues]);

  const rules: Record<string, RegisterOptions<IssueTypeFormDto>> = {
    name: {
      required: t(
        'settings:issueTypes.form.issueType.errorMessages.name.required',
      ),
    },
    parent: {
      required: t(
        'settings:issueTypes.form.issueType.errorMessages.parent.required',
      ),
    },
  };

  const issueTypeOptions = (
    issueTypesQuery.data?.pages
      .map((page: { data: Array<{ id: string; name: string }> }) => page.data)
      .flat() ?? []
  ).map((issueType: { id: string; name: string }) => ({
    value: issueType.id,
    label: localizedIssueTypes[issueType.name as IssueType] ?? issueType.name,
  }));

  const extendedIssueTypeOptions = useMemo(
    () =>
      [
        ...(subtypeResponse && subtypeResponse.issueType
          ? [
              {
                value: subtypeResponse.issueType.id,
                label:
                  localizedIssueTypes[
                    (subtypeResponse.issueType.name as unknown as IssueType) ??
                      'issue'
                  ] ?? subtypeResponse.issueType.name!,
              },
            ]
          : []),
      ].concat(issueTypeOptions),
    [subtypeDataUpdatedAt, issueTypeOptions],
  );

  const isTypeWithoutSubtype = hasId && !subtypeId;

  return (
    <Stack
      data-testid='add-category-form'
      px={4}
      py={5}
      spacing={4}
      width='50%'
    >
      {isLoading ? (
        <LoadingProgress height={350} />
      ) : (
        <>
          {isTypeWithoutSubtype ? null : (
            <ControlledTextField
              name='name'
              label={t(
                'settings:issueTypes.form.issueType.labels.issueSubtype',
              )}
              control={form.control}
              rules={rules.name}
              data-testid='title'
              maxLength={SUBTYPE_NAME_LENGTH_MAX}
            />
          )}
          {isTypeWithoutSubtype ? (
            <ControlledTextField
              name='name'
              label={t('settings:issueTypes.form.issueType.labels.issueType')}
              control={form.control}
              data-testid='title'
              inputProps={{ readOnly: true }}
            />
          ) : (
            <ControlledTextField
              name='parent'
              label={t('settings:issueTypes.form.issueType.labels.issueType')}
              control={form.control}
              disabled={hasId}
              data-testid='categoryId'
              rules={rules.parent}
              options={extendedIssueTypeOptions}
              placeholder={t('common:inputs.listBox.empty')}
            />
          )}

          <Controller
            name='issueTypeManagers'
            control={form.control}
            render={({ field: { value, onChange } }) => (
              <TypeManagersSelector
                value={value}
                onChange={onChange}
                label={
                  <FormLabel component='legend'>
                    {t(
                      'settings:issueTypes.form.issueType.labels.issueTypeManagers',
                    )}
                  </FormLabel>
                }
              />
            )}
          />
          <ConfirmationModal
            isOpen={showConfirmationModal}
            onAccept={handleDeleteIssueType}
            onCancel={handleCloseConfirmationModal}
            title={t('settings:issueTypes.modals.deleteIssueSubtype.title')}
            description={t(
              'settings:issueTypes.modals.deleteIssueSubtype.description',
            )}
            acceptButtonProps={{
              loading: isDeleteLoading,
              text: isDeleteLoading
                ? t('settings:issueTypes.modals.deleteIssueSubtype.deleting')
                : t('settings:issueTypes.modals.deleteIssueSubtype.delete'),
              color: 'error',
            }}
            cancelButtonProps={{
              text: t('settings:issueTypes.modals.deleteIssueSubtype.cancel'),
              variant: 'text',
            }}
            swapButtons
          />
        </>
      )}
    </Stack>
  );
}

export default IssueTypeForm;
